import { useMemo, type JSX } from "react";
import { Color } from "three";
import { BUILDING_INFO, type BuildingFamily } from "@stahlfront/core";

/**
 * Family-specific primitive-compound mesh per spec §5.
 * Each silhouette is recognisable without text labels:
 *   • BARRACKS            – long block + pitched roof + flagpole + flag.
 *   • RESEARCH_CENTER     – cubic base + hemisphere dome + radio antenna.
 *   • RESOURCE_FACTORY    – warehouse box + tall chimney + roof vent.
 *   • ARMORY              – low bunker + thick slab roof + rivets.
 *   • OFFICER_HQ          – squat base + tall command tower + radar dish.
 *   • GROUND_FORCES_BASE  – motor pool depot + two parked vehicles with wheels.
 *   • AIR_FORCE_BASE      – concrete runway + control tower + rotating mast.
 *   • VEHICLE_HANGARS     – arched half-cylinder hangar + floor.
 *   • SALVAGE_YARD        – three tilted scrap boxes on a low slab.
 *   • AA_BASE             – sandbagged platform + central gun mount + 2 elevated barrels.
 *   • MILITARY_CENTER     – parade square + 4 corner posts + central podium.
 *
 * The compound hierarchy is wrapped in a single root <group>; callers attach
 * onPointerDown / onPointerOver to *that* root so raycast hits propagate up
 * cleanly without per-primitive listeners.
 */

interface BuildingMeshProps {
  readonly family: BuildingFamily;
  /** Lifts emissive when the hover state is true (subtle 0.18 boost). */
  readonly hovered?: boolean;
}

/** OKLCH hue → approximate sRGB Color via HSL fallback (chroma≈0.16, L≈0.55). */
function hueColor(hue: number, lightness = 0.55, saturation = 0.6): Color {
  const c = new Color();
  c.setHSL(hue / 360, saturation, lightness);
  return c;
}

/** Darker variant of the family hue, used for accent details (flags, rims, guns). */
function hueAccent(hue: number): Color {
  return hueColor(hue, 0.38, 0.65);
}

/** Neutral steel grey used for shared structural masses across all families. */
function steel(lightness = 0.45): Color {
  const c = new Color();
  c.setHSL(0, 0, lightness);
  return c;
}

export function BuildingMesh({ family, hovered = false }: BuildingMeshProps): JSX.Element {
  const meta = BUILDING_INFO[family];
  const { primary, accent, dark } = useMemo(
    () => ({
      primary: hueColor(meta.accentHue, 0.5, 0.5),
      accent: hueAccent(meta.accentHue),
      dark: steel(0.32),
    }),
    [meta.accentHue],
  );

  const emissive = primary;
  const emissiveIntensity = hovered ? 0.18 : 0;

  switch (family) {
    case "BARRACKS":
      return (
        <group>
          {/* Long quartering block. */}
          <mesh castShadow receiveShadow position={[0, 0.45, 0]}>
            <boxGeometry args={[2.4, 0.9, 1.2]} />
            <meshStandardMaterial color={steel(0.55)} roughness={0.85} metalness={0.05} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Pitched roof — long ridge along x, two angled slabs. */}
          <mesh castShadow position={[0, 1.05, -0.32]} rotation={[Math.PI / 7, 0, 0]}>
            <boxGeometry args={[2.4, 0.06, 0.88]} />
            <meshStandardMaterial color={primary} roughness={0.7} metalness={0.1} />
          </mesh>
          <mesh castShadow position={[0, 1.05, 0.32]} rotation={[-Math.PI / 7, 0, 0]}>
            <boxGeometry args={[2.4, 0.06, 0.88]} />
            <meshStandardMaterial color={primary} roughness={0.7} metalness={0.1} />
          </mesh>
          {/* Flagpole + small banner, on the building's right edge. */}
          <mesh position={[1.05, 1.55, 0]}>
            <cylinderGeometry args={[0.025, 0.025, 1.0, 10]} />
            <meshStandardMaterial color={dark} roughness={0.6} metalness={0.4} />
          </mesh>
          <mesh position={[1.22, 1.85, 0]}>
            <planeGeometry args={[0.32, 0.22]} />
            <meshStandardMaterial color={accent} side={2} roughness={0.6} />
          </mesh>
        </group>
      );

    case "RESEARCH_CENTER":
      return (
        <group>
          <mesh castShadow receiveShadow position={[0, 0.3, 0]}>
            <boxGeometry args={[1.6, 0.6, 1.6]} />
            <meshStandardMaterial color={steel(0.6)} roughness={0.85} metalness={0.04} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Hemisphere dome. */}
          <mesh castShadow position={[0, 0.6, 0]}>
            <sphereGeometry args={[0.85, 28, 18, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={primary} roughness={0.45} metalness={0.25} />
          </mesh>
          {/* Radio antenna. */}
          <mesh position={[0, 1.7, 0]}>
            <cylinderGeometry args={[0.018, 0.018, 0.7, 8]} />
            <meshStandardMaterial color={dark} roughness={0.5} metalness={0.6} />
          </mesh>
          <mesh position={[0, 2.05, 0]}>
            <sphereGeometry args={[0.05, 10, 8]} />
            <meshStandardMaterial color={accent} roughness={0.4} metalness={0.4} />
          </mesh>
        </group>
      );

    case "RESOURCE_FACTORY":
      return (
        <group>
          <mesh castShadow receiveShadow position={[0, 0.5, 0]}>
            <boxGeometry args={[2.0, 1.0, 1.4]} />
            <meshStandardMaterial color={steel(0.5)} roughness={0.9} metalness={0.04} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Saw-tooth roof: 4 small angled slabs along x. */}
          {[-0.75, -0.25, 0.25, 0.75].map((dx) => (
            <mesh key={dx} castShadow position={[dx, 1.08, 0]} rotation={[Math.PI / 6, 0, 0]}>
              <boxGeometry args={[0.45, 0.04, 0.7]} />
              <meshStandardMaterial color={primary} roughness={0.7} metalness={0.1} />
            </mesh>
          ))}
          {/* Tall chimney with rim. */}
          <mesh castShadow position={[-0.85, 1.7, 0.55]}>
            <cylinderGeometry args={[0.16, 0.2, 1.5, 14]} />
            <meshStandardMaterial color={dark} roughness={0.85} metalness={0.2} />
          </mesh>
          <mesh position={[-0.85, 2.5, 0.55]}>
            <torusGeometry args={[0.18, 0.02, 8, 18]} />
            <meshStandardMaterial color={accent} roughness={0.5} metalness={0.5} />
          </mesh>
        </group>
      );

    case "ARMORY":
      return (
        <group>
          {/* Heavy bunker mass. */}
          <mesh castShadow receiveShadow position={[0, 0.4, 0]}>
            <boxGeometry args={[2.0, 0.8, 1.6]} />
            <meshStandardMaterial color={steel(0.42)} roughness={0.95} metalness={0.05} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Thick slab roof. */}
          <mesh castShadow position={[0, 0.85, 0]}>
            <boxGeometry args={[2.3, 0.16, 1.85]} />
            <meshStandardMaterial color={primary} roughness={0.75} metalness={0.2} />
          </mesh>
          {/* Rivet posts on roof corners. */}
          {[[-0.95, -0.78], [0.95, -0.78], [-0.95, 0.78], [0.95, 0.78]].map((p, i) => {
            const [x, z] = p as [number, number];
            return (
              <mesh key={i} position={[x, 1.0, z]}>
                <cylinderGeometry args={[0.07, 0.07, 0.16, 10]} />
                <meshStandardMaterial color={dark} roughness={0.7} metalness={0.4} />
              </mesh>
            );
          })}
        </group>
      );

    case "OFFICER_HQ":
      return (
        <group>
          {/* Squat administrative base. */}
          <mesh castShadow receiveShadow position={[0, 0.3, 0]}>
            <boxGeometry args={[1.6, 0.6, 1.6]} />
            <meshStandardMaterial color={steel(0.55)} roughness={0.85} metalness={0.06} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Command tower. */}
          <mesh castShadow position={[0, 1.4, 0]}>
            <boxGeometry args={[1.0, 1.6, 1.0]} />
            <meshStandardMaterial color={primary} roughness={0.7} metalness={0.15} />
          </mesh>
          {/* Roof crown. */}
          <mesh castShadow position={[0, 2.28, 0]}>
            <boxGeometry args={[1.1, 0.06, 1.1]} />
            <meshStandardMaterial color={accent} roughness={0.5} metalness={0.5} />
          </mesh>
          {/* Radar dish. */}
          <mesh position={[0, 2.5, 0]} rotation={[Math.PI / 6, 0, 0]}>
            <coneGeometry args={[0.18, 0.1, 14, 1, true]} />
            <meshStandardMaterial color={dark} roughness={0.45} metalness={0.5} />
          </mesh>
          {/* Antenna mast. */}
          <mesh position={[0, 2.7, 0]}>
            <cylinderGeometry args={[0.02, 0.02, 0.3, 8]} />
            <meshStandardMaterial color={dark} />
          </mesh>
        </group>
      );

    case "GROUND_FORCES_BASE":
      return (
        <group>
          {/* Depot building. */}
          <mesh castShadow receiveShadow position={[0, 0.35, -0.3]}>
            <boxGeometry args={[2.2, 0.7, 1.0]} />
            <meshStandardMaterial color={steel(0.5)} roughness={0.85} metalness={0.05} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Depot roof — single slope. */}
          <mesh castShadow position={[0, 0.78, -0.3]} rotation={[-Math.PI / 11, 0, 0]}>
            <boxGeometry args={[2.3, 0.05, 1.1]} />
            <meshStandardMaterial color={primary} roughness={0.7} />
          </mesh>
          {/* Two parked vehicles in front. */}
          {[-0.55, 0.55].map((x) => (
            <group key={x} position={[x, 0, 0.65]}>
              <mesh castShadow position={[0, 0.22, 0]}>
                <boxGeometry args={[0.7, 0.35, 0.45]} />
                <meshStandardMaterial color={primary} roughness={0.6} metalness={0.2} />
              </mesh>
              <mesh position={[0, 0.4, 0]}>
                <boxGeometry args={[0.42, 0.18, 0.4]} />
                <meshStandardMaterial color={accent} roughness={0.55} metalness={0.25} />
              </mesh>
              {/* Wheels. */}
              {[
                [-0.27, -0.18],
                [0.27, -0.18],
                [-0.27, 0.18],
                [0.27, 0.18],
              ].map((p, i) => {
                const [px, pz] = p as [number, number];
                return (
                  <mesh key={i} position={[px, 0.1, pz]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.1, 0.1, 0.08, 12]} />
                    <meshStandardMaterial color={dark} roughness={0.85} metalness={0.1} />
                  </mesh>
                );
              })}
            </group>
          ))}
        </group>
      );

    case "AIR_FORCE_BASE":
      return (
        <group>
          {/* Concrete runway slab — long on z. */}
          <mesh receiveShadow position={[0, 0.05, 0]}>
            <boxGeometry args={[1.2, 0.08, 3.2]} />
            <meshStandardMaterial color={steel(0.62)} roughness={0.95} metalness={0.02} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Centerline stripes. */}
          {[-1.1, -0.5, 0.1, 0.7, 1.3].map((dz) => (
            <mesh key={dz} position={[0, 0.094, dz]}>
              <boxGeometry args={[0.08, 0.012, 0.22]} />
              <meshStandardMaterial color={"#ffffff"} roughness={0.8} metalness={0.0} />
            </mesh>
          ))}
          {/* Control tower. */}
          <mesh castShadow position={[-0.95, 0.7, -0.6]}>
            <boxGeometry args={[0.6, 1.4, 0.6]} />
            <meshStandardMaterial color={primary} roughness={0.7} metalness={0.15} />
          </mesh>
          {/* Tower observation cap. */}
          <mesh castShadow position={[-0.95, 1.5, -0.6]}>
            <boxGeometry args={[0.85, 0.18, 0.85]} />
            <meshStandardMaterial color={accent} roughness={0.45} metalness={0.4} />
          </mesh>
          {/* Rotating-radar mast (static for now). */}
          <mesh position={[-0.95, 1.85, -0.6]}>
            <cylinderGeometry args={[0.02, 0.02, 0.35, 8]} />
            <meshStandardMaterial color={dark} />
          </mesh>
          <mesh position={[-0.95, 2.0, -0.6]} rotation={[0, 0, Math.PI / 2]}>
            <boxGeometry args={[0.04, 0.4, 0.04]} />
            <meshStandardMaterial color={dark} />
          </mesh>
        </group>
      );

    case "VEHICLE_HANGARS":
      return (
        <group>
          {/* Floor base. */}
          <mesh receiveShadow position={[0, 0.05, 0]}>
            <boxGeometry args={[2.4, 0.1, 1.6]} />
            <meshStandardMaterial color={steel(0.52)} roughness={0.95} metalness={0.04} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Arched hangar shell — half cylinder lying on its side. */}
          <mesh
            castShadow
            position={[0, 0.1, 0]}
            rotation={[0, 0, Math.PI / 2]}
          >
            <cylinderGeometry args={[0.85, 0.85, 2.4, 28, 1, true, 0, Math.PI]} />
            <meshStandardMaterial color={primary} roughness={0.55} metalness={0.3} side={2} />
          </mesh>
          {/* Front gable. */}
          <mesh position={[1.21, 0.5, 0]} rotation={[0, Math.PI / 2, 0]}>
            <circleGeometry args={[0.85, 18, 0, Math.PI]} />
            <meshStandardMaterial color={accent} roughness={0.6} side={2} />
          </mesh>
          {/* Rear gable. */}
          <mesh position={[-1.21, 0.5, 0]} rotation={[0, -Math.PI / 2, 0]}>
            <circleGeometry args={[0.85, 18, 0, Math.PI]} />
            <meshStandardMaterial color={accent} roughness={0.6} side={2} />
          </mesh>
        </group>
      );

    case "SALVAGE_YARD":
      return (
        <group>
          {/* Concrete pad. */}
          <mesh receiveShadow position={[0, 0.05, 0]}>
            <boxGeometry args={[2.0, 0.1, 1.6]} />
            <meshStandardMaterial color={steel(0.5)} roughness={0.95} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Scrap pile — 4 boxes stacked tilted. */}
          <mesh castShadow position={[-0.55, 0.32, 0.1]} rotation={[0.2, 0.4, 0.1]}>
            <boxGeometry args={[0.8, 0.55, 0.6]} />
            <meshStandardMaterial color={primary} roughness={0.85} metalness={0.2} />
          </mesh>
          <mesh castShadow position={[0.2, 0.4, -0.2]} rotation={[-0.15, -0.3, 0.25]}>
            <boxGeometry args={[0.7, 0.7, 0.55]} />
            <meshStandardMaterial color={accent} roughness={0.85} metalness={0.25} />
          </mesh>
          <mesh castShadow position={[0.65, 0.28, 0.35]} rotation={[0.25, 0.6, -0.1]}>
            <boxGeometry args={[0.55, 0.5, 0.5]} />
            <meshStandardMaterial color={dark} roughness={0.9} metalness={0.15} />
          </mesh>
          <mesh castShadow position={[-0.05, 0.78, 0.05]} rotation={[0.4, 0.2, 0.3]}>
            <boxGeometry args={[0.5, 0.4, 0.45]} />
            <meshStandardMaterial color={primary} roughness={0.85} metalness={0.2} />
          </mesh>
        </group>
      );

    case "AA_BASE":
      return (
        <group>
          {/* Sandbagged platform. */}
          <mesh castShadow receiveShadow position={[0, 0.15, 0]}>
            <boxGeometry args={[1.6, 0.3, 1.6]} />
            <meshStandardMaterial color={steel(0.42)} roughness={0.92} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Sandbag rim — 4 short cylinders along edges. */}
          {[
            [0, -0.7, 0.0, 1.4],
            [0, 0.7, 0.0, 1.4],
            [-0.7, 0, Math.PI / 2, 1.4],
            [0.7, 0, Math.PI / 2, 1.4],
          ].map((p, i) => {
            const [px, pz, rotY, len] = p as [number, number, number, number];
            return (
              <mesh key={i} position={[px, 0.36, pz]} rotation={[Math.PI / 2, 0, rotY]}>
                <cylinderGeometry args={[0.1, 0.1, len, 12]} />
                <meshStandardMaterial color={primary} roughness={0.95} metalness={0.05} />
              </mesh>
            );
          })}
          {/* Central gun mount + cone gun. */}
          <mesh castShadow position={[0, 0.55, 0]}>
            <cylinderGeometry args={[0.32, 0.36, 0.45, 18]} />
            <meshStandardMaterial color={accent} roughness={0.5} metalness={0.5} />
          </mesh>
          {/* Twin barrels at ~30° elevation. */}
          {[-0.12, 0.12].map((dx) => (
            <mesh key={dx} castShadow position={[dx, 1.0, 0.32]} rotation={[Math.PI / 2 - Math.PI / 6, 0, 0]}>
              <cylinderGeometry args={[0.05, 0.05, 0.95, 10]} />
              <meshStandardMaterial color={dark} roughness={0.45} metalness={0.7} />
            </mesh>
          ))}
        </group>
      );

    case "MILITARY_CENTER":
      return (
        <group>
          {/* Parade square. */}
          <mesh receiveShadow position={[0, 0.05, 0]}>
            <boxGeometry args={[2.4, 0.1, 2.4]} />
            <meshStandardMaterial color={steel(0.6)} roughness={0.92} emissive={emissive} emissiveIntensity={emissiveIntensity} />
          </mesh>
          {/* Border stripe. */}
          <mesh position={[0, 0.105, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[1.05, 1.18, 32]} />
            <meshStandardMaterial color={primary} roughness={0.85} side={2} />
          </mesh>
          {/* 4 corner posts. */}
          {[
            [-1.1, -1.1],
            [1.1, -1.1],
            [-1.1, 1.1],
            [1.1, 1.1],
          ].map((p, i) => {
            const [x, z] = p as [number, number];
            return (
              <mesh key={i} castShadow position={[x, 0.85, z]}>
                <cylinderGeometry args={[0.08, 0.08, 1.5, 10]} />
                <meshStandardMaterial color={accent} roughness={0.6} metalness={0.4} />
              </mesh>
            );
          })}
          {/* Central podium. */}
          <mesh castShadow position={[0, 0.3, 0]}>
            <boxGeometry args={[0.7, 0.45, 0.7]} />
            <meshStandardMaterial color={primary} roughness={0.7} metalness={0.15} />
          </mesh>
          {/* Banner staff. */}
          <mesh position={[0, 0.95, 0]}>
            <cylinderGeometry args={[0.025, 0.025, 1.0, 10]} />
            <meshStandardMaterial color={dark} />
          </mesh>
          <mesh position={[0.18, 1.25, 0]}>
            <planeGeometry args={[0.32, 0.22]} />
            <meshStandardMaterial color={accent} side={2} roughness={0.55} />
          </mesh>
        </group>
      );
  }
}
